import { lazy, Suspense, ComponentType, ReactNode } from 'react';
import { Skeleton } from 'primereact/skeleton';

import { retryLazyImport } from './utils/lazyWithRetry';

const SkeletonFallback = () => (
  <div className="w-full surface-border p-2">
    <Skeleton width="100%" height="20rem" className="mb-2" />
    <Skeleton width="100%" height="20rem" />
  </div>
);

export const EmptyFallback = () => <></>;

function createLazyComponent<T extends object>(
  importFunc: () => Promise<{ default: ComponentType<T> }>,
  fallback: ReactNode
): ComponentType<T> {
  const LazyComponent = lazy(retryLazyImport(importFunc));

  const LazyWrappedComponent = (props: T) => (
    <Suspense fallback={fallback}>
      <LazyComponent {...props} />
    </Suspense>
  );

  return LazyWrappedComponent;
}

// Export variations
export const withLazyLoad = <T extends object>(
  importFunc: () => Promise<{ default: ComponentType<T> }>
): ComponentType<T> => createLazyComponent(importFunc, <SkeletonFallback />);

export const withLazyLoadWithoutFallback = <T extends object>(
  importFunc: () => Promise<{ default: ComponentType<T> }>
): ComponentType<T> => createLazyComponent(importFunc, <EmptyFallback />);
